/**
 * Procedure consent forms (dental + eye program, phase A4).
 *
 * Library:  /api/consent-templates       — the clinic's forms and the ClinVea library (doctors, clinic admins, desk read).
 * Staff:    /api/procedure-consents      — present, sign, send to the patient's app, decline, withdraw, audit, PDF.
 *                                          Doctors and desk/nurse staff only: clinic admins are refused (medopd-docs 77).
 * Patients: /api/patient/procedure-consents — forms sent to the app; sign or decline; PDF of a signed one.
 *
 * Shapes mirror medopd-backend consentform/ConsentFormDtos.java exactly.
 */
import { fetchApi, fetchPatientApi, API_BASE_URL, generateRequestId } from '../client';
import type { ApiResponse } from '../client';
import { buildQS } from '../helpers';

const TEMPLATES = '/api/consent-templates';
const CONSENTS = '/api/procedure-consents';
const PATIENT_CONSENTS = '/api/patient/procedure-consents';

export type ConsentLang = 'en' | 'te' | 'hi';
export type ConsentSpecialty = 'GENERAL' | 'DENTAL' | 'EYE';
export type TemplateStatus = 'DRAFT' | 'PUBLISHED' | 'RETIRED';
export type ReviewStatus = 'NEEDS_REVIEW' | 'REVIEWED';
export type ConsentStatus = 'PENDING' | 'SIGNED' | 'DECLINED' | 'CANCELLED';
export type SignerType = 'PATIENT' | 'GUARDIAN';
export type SignedVia = 'CLINIC_DEVICE' | 'PATIENT_APP';

export interface TemplateSummary {
  id: string;
  code: string | null;
  specialty: ConsentSpecialty | string | null;
  title: string;
  /** A ClinVea library form (read-only; copy it or "Review and use"). */
  global: boolean;
  status: TemplateStatus;
  reviewStatus: ReviewStatus;
  version: number;
  languages: ConsentLang[];
  copiedFromId: string | null;
  procedureKeywords: string | null;
  procedureCatalogueIds: string[];
  requiresWitness: boolean;
  updatedAt: string | null;
}

export interface LanguageText {
  title: string | null;
  body: string | null;
  /** One per line. */
  risks: string | null;
  /** One per line. */
  alternatives: string | null;
}

export interface TemplateDetail {
  summary: TemplateSummary;
  en: LanguageText | null;
  te: LanguageText | null;
  hi: LanguageText | null;
  reviewedByName: string | null;
  reviewedAt: string | null;
  unknownPlaceholders: string[];
  timesUsed: number;
}

export interface TemplateRequest {
  copyFromId?: string;
  code?: string;
  specialty?: string;
  title?: string;
  body?: string;
  risks?: string;
  alternatives?: string;
  titleTe?: string;
  bodyTe?: string;
  risksTe?: string;
  alternativesTe?: string;
  titleHi?: string;
  bodyHi?: string;
  risksHi?: string;
  alternativesHi?: string;
  procedureKeywords?: string;
  procedureCatalogueIds?: string[];
  requiresWitness?: boolean;
  status?: 'PUBLISHED' | 'DRAFT';
}

/** The filled-in wording, as the patient reads it. */
export interface Preview {
  templateId: string;
  templateVersion: number;
  language: ConsentLang;
  languages: ConsentLang[];
  title: string;
  body: string;
  risks: string[];
  alternatives: string[];
  requiresWitness: boolean;
  reviewStatus: ReviewStatus;
}

export interface ConsentSummary {
  id: string;
  status: ConsentStatus;
  title: string;
  templateCode: string | null;
  templateVersion: number;
  language: ConsentLang;
  procedureName: string | null;
  site: string | null;
  doctorName: string | null;
  clinicalProcedureId: string | null;
  planItemId: string | null;
  visitId: string | null;
  patientId: string;
  /** Sent on the waiting list only. */
  patientName: string | null;
  requestedByName: string | null;
  requestedAt: string | null;
  sentToPatientAt: string | null;
  signerType: SignerType | null;
  signerName: string | null;
  signerRelationship: string | null;
  witnessName: string | null;
  signedAt: string | null;
  signedVia: SignedVia | null;
  declinedReason: string | null;
  cancelledReason: string | null;
  reviewStatusAtUse: ReviewStatus | null;
}

export interface ConsentView {
  summary: ConsentSummary;
  wording: Preview;
  /** PNG data URL, SIGNED only. */
  signatureDataUrl: string | null;
  procedureLinked: boolean;
  procedureNote: string | null;
}

export interface AuditEntry { action: string; actorRole: string | null; summary: string; at: string }

export interface ConsentRequest {
  patientId: string;
  templateId: string;
  language?: ConsentLang;
  visitId?: string;
  clinicalProcedureId?: string;
  planItemId?: string;
  procedureName?: string;
  site?: string;
  doctorUserId?: string;
  sendToPatient?: boolean;
}

export interface SignaturePayload { width: number; height: number; strokes: { x: number; y: number }[][] }

export interface SignRequest {
  language: ConsentLang;
  /** The wording.templateVersion that was shown; the server refuses (409) if the wording changed meanwhile. */
  templateVersion: number;
  signerType: SignerType;
  signerName?: string;
  signerRelationship?: string;
  signature: SignaturePayload;
  agreed: true;
  device?: string;
}

export interface PreviewParams { language?: ConsentLang; patientId?: string; procedureName?: string; site?: string }

const json = (method: string, body?: unknown): RequestInit =>
  body === undefined ? { method } : { method, body: JSON.stringify(body) };
const id = (v: string) => encodeURIComponent(v);

// ── PDF (auth headers exactly as fetchApi / fetchPatientApi send them) ───────────────────────────────────────────

export type PdfResult = ApiResponse<{ blob: Blob; fileName?: string }>;

async function blobFrom(response: Response): Promise<PdfResult> {
  if (!response.ok) {
    const isJson = response.headers.get('content-type')?.includes('application/json');
    const err = isJson ? await response.json().catch(() => ({})) : {};
    return { error: (err as { message?: string }).message || 'We couldn’t open the form. Please try again.', status: response.status };
  }
  const blob = await response.blob();
  const match = (response.headers.get('content-disposition') || '').match(/filename="?([^";]+)"?/);
  return { data: { blob, fileName: match?.[1] } };
}

async function staffPdf(consentId: string): Promise<PdfResult> {
  const headers: Record<string, string> = { 'X-Request-ID': generateRequestId(), 'X-Client-Version': '1.0.0' };
  const sessionToken = sessionStorage.getItem('sessionToken');
  if (sessionToken) headers['X-Session-Token'] = sessionToken;
  const clinic = sessionStorage.getItem('active_clinic_id');
  if (clinic) headers['X-Active-Clinic-Id'] = clinic;
  try {
    return await blobFrom(await fetch(`${API_BASE_URL}${CONSENTS}/${id(consentId)}/pdf`, { method: 'GET', headers, credentials: 'include' }));
  } catch {
    return { error: 'Network error. Please check your connection.' };
  }
}

async function patientPdf(consentId: string): Promise<PdfResult> {
  const headers: Record<string, string> = { 'X-Request-ID': generateRequestId(), 'X-Auth-Transport': 'cookie' };
  const clinic = sessionStorage.getItem('active_patient_clinic_id');
  if (clinic) headers['X-Active-Patient-Clinic-Id'] = clinic;
  const dependent = sessionStorage.getItem('active_dependent_patient_id');
  if (dependent) headers['X-Active-Dependent-Patient-Id'] = dependent;
  try {
    return await blobFrom(await fetch(`${API_BASE_URL}${PATIENT_CONSENTS}/${id(consentId)}/pdf`, { method: 'GET', headers, credentials: 'include' }));
  } catch {
    return { error: 'Network error. Please check your connection.' };
  }
}

// ── Library ──────────────────────────────────────────────────────────────────────────────────────────────────────

export const consentTemplatesModule = {
  /** usable=true → published forms only (the desk's picker). */
  list: (usable = false) => fetchApi<TemplateSummary[]>(`${TEMPLATES}${buildQS({ usable })}`),
  suggest: (procedureName?: string, catalogueId?: string) =>
    fetchApi<TemplateSummary[]>(`${TEMPLATES}/suggest${buildQS({ procedureName, catalogueId })}`),
  get: (templateId: string) => fetchApi<TemplateDetail>(`${TEMPLATES}/${id(templateId)}`),
  preview: (templateId: string, p: PreviewParams = {}) =>
    fetchApi<Preview>(`${TEMPLATES}/${id(templateId)}/preview${buildQS({ ...p })}`),
  create: (body: TemplateRequest) => fetchApi<TemplateDetail>(TEMPLATES, json('POST', body)),
  update: (templateId: string, body: TemplateRequest) => fetchApi<TemplateDetail>(`${TEMPLATES}/${id(templateId)}`, json('PUT', body)),
  /** Doctor only. On a library form this creates the clinic's own reviewed, published copy (a new id). */
  review: (templateId: string) => fetchApi<TemplateDetail>(`${TEMPLATES}/${id(templateId)}/review`, json('POST')),
  publish: (templateId: string) => fetchApi<TemplateDetail>(`${TEMPLATES}/${id(templateId)}/publish`, json('POST')),
  retire: (templateId: string) => fetchApi<TemplateDetail>(`${TEMPLATES}/${id(templateId)}/retire`, json('POST')),
};

// ── Staff: present and sign ──────────────────────────────────────────────────────────────────────────────────────

export const procedureConsentsModule = {
  request: (body: ConsentRequest) => fetchApi<ConsentView>(CONSENTS, json('POST', body)),
  waiting: () => fetchApi<ConsentSummary[]>(`${CONSENTS}/waiting`),
  forPatient: (patientId: string) => fetchApi<ConsentSummary[]>(`${CONSENTS}/patient/${id(patientId)}`),
  forProcedure: (procedureId: string) => fetchApi<ConsentSummary[]>(`${CONSENTS}/procedure/${id(procedureId)}`),
  get: (consentId: string, language?: ConsentLang) => fetchApi<ConsentView>(`${CONSENTS}/${id(consentId)}${buildQS({ language })}`),
  sign: (consentId: string, body: SignRequest) => fetchApi<ConsentView>(`${CONSENTS}/${id(consentId)}/sign`, json('POST', body)),
  send: (consentId: string) => fetchApi<ConsentView>(`${CONSENTS}/${id(consentId)}/send`, json('POST')),
  decline: (consentId: string, reason?: string) => fetchApi<ConsentView>(`${CONSENTS}/${id(consentId)}/decline`, json('POST', { reason })),
  cancel: (consentId: string, reason: string) => fetchApi<ConsentView>(`${CONSENTS}/${id(consentId)}/cancel`, json('POST', { reason })),
  audit: (consentId: string) => fetchApi<AuditEntry[]>(`${CONSENTS}/${id(consentId)}/audit`),
  pdf: staffPdf,
};

// ── Patient portal ───────────────────────────────────────────────────────────────────────────────────────────────

export const patientProcedureConsentsModule = {
  list: () => fetchPatientApi<ConsentSummary[]>(PATIENT_CONSENTS),
  get: (consentId: string, language?: ConsentLang) =>
    fetchPatientApi<ConsentView>(`${PATIENT_CONSENTS}/${id(consentId)}${buildQS({ language })}`),
  sign: (consentId: string, body: SignRequest) =>
    fetchPatientApi<ConsentView>(`${PATIENT_CONSENTS}/${id(consentId)}/sign`, json('POST', body)),
  decline: (consentId: string, reason?: string) =>
    fetchPatientApi<ConsentView>(`${PATIENT_CONSENTS}/${id(consentId)}/decline`, json('POST', { reason })),
  pdf: patientPdf,
};
